/**
 * GlobalView — Selection Marker (Spy-Style Scan Reticle)
 *
 * Animated HUD targeting reticle that appears over selected entities.
 * Projects the entity's 3D position to screen coordinates each frame
 * and renders a tactical scan marker with:
 * - 4 corner brackets
 * - Rotating dashed scan ring
 * - Pulsing center dot + crosshairs
 * - Status label ("SCANNING…" → "LOCKED")
 * - Coordinate readout
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import * as Cesium from 'cesium';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { getInterpolatedPosition } from '../../services/layers/AirLayerRenderer';
import { getSatellitePosition } from '../../services/layers/SpaceLayerRenderer';
import { getRailBillboardPosition } from '../../services/layers/RailLayerRenderer';
import styles from './SelectionMarker.module.css';

/** Duration (ms) before "SCANNING…" transitions to "LOCKED" */
const LOCK_DELAY_MS = 1200;

export default function SelectionMarker() {
  const viewer = useGlobeStore((s) => s.viewer);
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );

  const markerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const [locked, setLocked] = useState(false);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const lockTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // ─── Track previous entity to handle exit animation ──────────
  const prevEntityIdRef = useRef<string | null>(null);

  // ─── Position state for coordinate readout (direct DOM ref, no React re-render) ──
  const readoutRef = useRef<HTMLDivElement>(null);

  // ─── Open marker when entity selected ────────────────────────
  useEffect(() => {
    if (selectedEntityId && entity) {
      // New selection
      setExiting(false);
      setVisible(true);
      setLocked(false);

      // Start lock timer
      lockTimerRef.current = setTimeout(() => setLocked(true), LOCK_DELAY_MS);
      prevEntityIdRef.current = selectedEntityId;
    } else if (prevEntityIdRef.current && !selectedEntityId) {
      // Deselected → exit animation
      setExiting(true);
      setLocked(false);
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);

      const exitTimer = setTimeout(() => {
        setVisible(false);
        setExiting(false);
        prevEntityIdRef.current = null;
      }, 200); // matches markerFadeOut duration
      return () => clearTimeout(exitTimer);
    }

    return () => {
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    };
  }, [selectedEntityId, entity]);

  // ─── Per-frame screen-space tracking ─────────────────────────
  const updatePosition = useCallback(() => {
    if (!viewer || viewer.isDestroyed() || !markerRef.current) {
      rafRef.current = requestAnimationFrame(updatePosition);
      return;
    }

    const entityId = selectedEntityId || prevEntityIdRef.current;
    if (!entityId) {
      rafRef.current = requestAnimationFrame(updatePosition);
      return;
    }

    // Resolve the actual rendered position from the correct layer renderer.
    // Each renderer may use different positions (snapToTrack, SGP4 propagation,
    // dead-reckoning interpolation) that differ from raw entity store positions.
    let cartesian: Cesium.Cartesian3 | null = null;
    let lat = 0;
    let lon = 0;

    if (entityId.startsWith('rail-')) {
      // Rail: use billboard position (snapToTrack + 450m altitude)
      cartesian = getRailBillboardPosition(entityId);
    } else if (entityId.startsWith('space-')) {
      // Satellite: use SGP4-propagated billboard position
      cartesian = getSatellitePosition(entityId);
    } else {
      // Aircraft: try interpolated position first (dead-reckoning)
      const interpolated = getInterpolatedPosition(entityId);
      if (interpolated) {
        cartesian = Cesium.Cartesian3.fromDegrees(
          interpolated.lon, interpolated.lat, interpolated.alt,
        );
      }
    }

    // Fallback: use raw entity store position
    if (!cartesian) {
      const entityData = useEntityStore.getState().entities.get(entityId);
      if (entityData) {
        cartesian = Cesium.Cartesian3.fromDegrees(
          entityData.position.lon, entityData.position.lat, entityData.position.alt,
        );
      }
    }

    // Extract lat/lon for coordinate readout
    if (cartesian) {
      const carto = Cesium.Cartographic.fromCartesian(cartesian);
      lat = Cesium.Math.toDegrees(carto.latitude);
      lon = Cesium.Math.toDegrees(carto.longitude);
    }

    if (cartesian) {
      const screenPos = Cesium.SceneTransforms.worldToWindowCoordinates(
        viewer.scene, cartesian,
      );

      if (screenPos) {
        // worldToWindowCoordinates returns canvas-relative coordinates,
        // which matches the marker's positioned parent (globe container).
        markerRef.current.style.left = `${screenPos.x}px`;
        markerRef.current.style.top = `${screenPos.y}px`;
        markerRef.current.style.display = '';

        // Update coordinate readout via DOM (no React re-render)
        if (readoutRef.current) {
          readoutRef.current.textContent = `${lat.toFixed(4)}° / ${lon.toFixed(4)}°`;
        }
      } else {
        // Entity is behind the camera
        markerRef.current.style.display = 'none';
      }
    }

    rafRef.current = requestAnimationFrame(updatePosition);
  }, [viewer, selectedEntityId]);

  useEffect(() => {
    if (visible) {
      rafRef.current = requestAnimationFrame(updatePosition);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [visible, updatePosition]);

  // ─── Don't render if nothing to show ─────────────────────────
  if (!visible) return null;

  const markerClasses = [
    styles.marker,
    exiting ? styles.markerExiting : '',
    locked ? styles.locked : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={markerRef} className={markerClasses} id="selection-marker">
      {/* Corner brackets */}
      <div className={styles.corners}>
        <div className={styles.corner} />
        <div className={styles.corner} />
        <div className={styles.corner} />
        <div className={styles.corner} />
      </div>

      {/* Rotating scan ring */}
      <div className={styles.scanRing} />

      {/* Sweep line */}
      <div className={styles.scanLine} />

      {/* Coordinate readout — updated via DOM ref, not React state */}
      <div ref={readoutRef} className={styles.coordReadout} />

      {/* Status label */}
      <div className={styles.statusLabel}>
        {locked ? '● LOCKED' : 'SCANNING…'}
      </div>
    </div>
  );
}
